import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { contactService } from '../services/contactService';
import { unitService } from '../services/unitService';
import { dealService } from '../services/dealService';
import { Contact } from '../types/contact';
import { Unit } from '../types/unit';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface DealFormSliderProps {
  workspaceId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

const DealFormSlider: React.FC<DealFormSliderProps> = ({
  workspaceId,
  onSuccess,
  onCancel
}) => {
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Search states
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [unitSearchResults, setUnitSearchResults] = useState<Unit[]>([]);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Projects
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (contactSearchQuery.length >= 2) {
      searchContacts();
    } else {
      setContactSearchResults([]);
      setShowContactDropdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSearchQuery]);

  useEffect(() => {
    if (unitSearchQuery.length >= 1 && selectedProjectId) {
      searchUnits();
    } else {
      setUnitSearchResults([]);
      setShowUnitDropdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSearchQuery, selectedProjectId]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const searchContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await contactService.getAll(
        workspaceId,
        { search: contactSearchQuery },
        1,
        10
      );
      setContactSearchResults(response.contacts);
      setShowContactDropdown(true);
    } catch (error) {
      console.error('Error searching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const searchUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await unitService.getAll(
        workspaceId,
        { search: unitSearchQuery, projectId: selectedProjectId, status: 'AVAILABLE' }
      );
      setUnitSearchResults(response.units);
      setShowUnitDropdown(true);
    } catch (error) {
      console.error('Error searching units:', error);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleSelectContact = (contact: Contact) => {
    if (!selectedContacts.find(c => c._id === contact._id)) {
      setSelectedContacts([...selectedContacts, contact]);
    }
    setContactSearchQuery('');
    setShowContactDropdown(false);
  };

  const handleRemoveContact = (contactId: string) => {
    setSelectedContacts(selectedContacts.filter(c => c._id !== contactId));
  };

  const handleSelectUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setUnitSearchQuery(`${unit.unitNo} - ${unit.modelName || ''}`);
    setShowUnitDropdown(false);
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProjectId(e.target.value);
    setSelectedUnit(null);
    setUnitSearchQuery('');
  };

  const handleCreateDeal = async () => {
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact');
      return;
    }

    if (!selectedUnit) {
      setError('Please select a unit');
      return;
    }

    try {
      setCreating(true);
      setError('');

      const dealData = {
        unitId: selectedUnit._id,
        projectId: selectedUnit.projectId,
        purchasers: selectedContacts.map((contact, index) => {
          const primaryEmail = contact.emails.find(e => e.isPrimary);
          const primaryPhone = contact.phones.find(p => p.isPrimary);
          return {
            contactId: contact._id,
            isPrimary: index === 0,
            displayName: `${contact.firstName} ${contact.lastName}`,
            email: primaryEmail?.email || contact.emails[0]?.email || '',
            phone: primaryPhone?.number || contact.phones[0]?.number || ''
          };
        }),
        dealType: 'PURCHASE',
        stage: 'NEW',
        initiatedOn: new Date().toISOString()
      };

      await dealService.create(workspaceId, dealData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create deal');
      console.error('Error creating deal:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onCancel} />
      <div className="drawer">
        <div className="drawer-header">
          <h2 className="drawer-title">Create New Deal</h2>
          <button className="drawer-close" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>

        <div className="drawer-content">
          {error && (
            <div className="form-error" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {/* Contacts Selection */}
          <div className="form-group">
            <label className="form-label">
              Purchaser(s)
            </label>

            {/* Selected Contacts */}
            {selectedContacts.length > 0 && (
              <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedContacts.map((contact, index) => (
                  <div
                    key={contact._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      background: '#F3F4F6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <span>
                      {contact.firstName} {contact.lastName}
                      {index === 0 && <span style={{ marginLeft: '4px', color: '#48182F', fontWeight: '600' }}>(Primary)</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveContact(contact._id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Contact Search Input */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search contacts by name or email..."
                value={contactSearchQuery}
                onChange={(e) => setContactSearchQuery(e.target.value)}
                onFocus={() => {
                  if (contactSearchResults.length > 0) {
                    setShowContactDropdown(true);
                  }
                }}
              />
              {loadingContacts && (
                <div style={{ position: 'absolute', right: '12px', top: '12px' }}>
                  <div className="builders-loading-spinner" style={{ width: '16px', height: '16px' }} />
                </div>
              )}

              {/* Contact Dropdown */}
              {showContactDropdown && contactSearchResults.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 10,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {contactSearchResults.map((contact) => {
                    const primaryEmail = contact.emails.find(e => e.isPrimary);
                    const emailToShow = primaryEmail?.email || contact.emails[0]?.email || 'No email';
                    return (
                      <div
                        key={contact._id}
                        onClick={() => handleSelectContact(contact)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #F3F4F6',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        <div style={{ fontWeight: '500' }}>
                          {contact.firstName} {contact.lastName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          {emailToShow}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
              First contact selected will be the primary purchaser
            </div>
          </div>

          {/* Project Selection */}
          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
              </div>
            ) : (
              <select
                id="projectId"
                className="form-input"
                value={selectedProjectId}
                onChange={handleProjectChange}
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Unit Selection */}
          <div className="form-group">
            <label className="form-label">
              Unit
            </label>

            {/* Selected Unit Display */}
            {selectedUnit && (
              <div style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    padding: '12px',
                    background: '#F9FAFB',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    Unit {selectedUnit.unitNo}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    {selectedUnit.modelName && `Model: ${selectedUnit.modelName}`}
                    {selectedUnit.floorMarketing && ` • Floor: ${selectedUnit.floorMarketing}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUnit(null);
                      setUnitSearchQuery('');
                    }}
                    style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: '#48182F',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    Change Unit
                  </button>
                </div>
              </div>
            )}

            {/* Unit Search Input */}
            {!selectedUnit && (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder={selectedProjectId ? "Search available units by number..." : "Select a project first"}
                  value={unitSearchQuery}
                  onChange={(e) => setUnitSearchQuery(e.target.value)}
                  onFocus={() => {
                    if (unitSearchResults.length > 0) {
                      setShowUnitDropdown(true);
                    }
                  }}
                  disabled={!selectedProjectId}
                />
                {loadingUnits && (
                  <div style={{ position: 'absolute', right: '12px', top: '12px' }}>
                    <div className="builders-loading-spinner" style={{ width: '16px', height: '16px' }} />
                  </div>
                )}

                {/* Unit Dropdown */}
                {showUnitDropdown && unitSearchResults.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {unitSearchResults.map((unit) => (
                      <div
                        key={unit._id}
                        onClick={() => handleSelectUnit(unit)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #F3F4F6',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        <div style={{ fontWeight: '500' }}>
                          Unit {unit.unitNo}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          {unit.modelName && `${unit.modelName}`}
                          {unit.floorMarketing && ` • Floor: ${unit.floorMarketing}`}
                          {unit.status && ` • ${unit.status}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="drawer-footer">
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={onCancel}
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-md"
            onClick={handleCreateDeal}
            disabled={creating || selectedContacts.length === 0 || !selectedUnit}
          >
            {creating ? 'Creating...' : 'Create Deal'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DealFormSlider;
